<script>
    import {tick} from 'svelte'
    import Util from 'util/util'
    import InputUtil from 'util/InputUtil'
    import Icon from '../icon/Icon.svelte'

    /**
     * @typedef {Object} Props
     * @property {string} [value] - Optional initial value of the input.
     * @property {string} [placeholder] - Optional placeholder value.
     * @property {string} [label] - Optional string to use as <label>.
     * @property {string} [formName]
     * @property {string} [type] - Input element type. Can only be set on initialization. Must be a text-like input
     * type: text, number, url, email, search. https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types
     * @property {boolean} [autoFocus] - Whether the input should claim focus after it has been initialized.
     * @property {boolean} [skipValidation] - Whether to skip content sanitization when calling
     * .getValue() (WARNING: USE WITH CARE!)
     * @property {boolean} [enableHTMLshortcuts] - If enabled, user can use key shortcuts to make selected text bold,
     * italicized, underlined, sub and super scripted.
     * @property {boolean} [noAutoCapitalize] - If true, disable automatic capitalization.
     * @property {boolean} [noAutoCorrect] - If true, disable autocorrect behaviour, including Grammarly suggestions.
     * @property {string} [autoCompleteTokens] - Optional string of space-separated tokens to hint to the browser and
     * password managers how to autocomplete this input field. For example "email username" will hint the browser to
     * look for a email or username. https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
     * @property {string} [inputMode] - Optional. Set specific type of virtual keyboard layout (text, tel, url, email,
     * numeric, decimal, search).This overrides the virtual keyboard set by the input element type.
     * @property {boolean} [disabled] - Whether input element is disabled.
     * @property {boolean} [isReadOnly] - Whether input element is in read-only mode.
     * @property {number} [maxLength] - If more than 0, enforce a maximum length to the input value and show the current
     * character count and max length to the user.
     * @property {number} [minLength] - Enforce minimum length to input value. Useful for
     * search inputs, which often require at least 3 characters.
     * @property {number} [min] - Enforce min and/or max value for number type input.
     * @property {number} [max]
     * @property {boolean} [hasUppercase] - If enabled display input in ALL CAPS.
     * @property {boolean} [displayInline] - If true, display as inline-flex instead of flex
     * and auto-resize to fit content.
     * @property {boolean} [isWide] - If true and combined with displayInline, min width of input is bigger.
     * @property {boolean} [isXL] - If true, supersize input field.
     * @property {Function} [submitCallback] - Function to call when user hits the enter key or clicks the
     * submit button.
     * @property {boolean} [showSubmitButton] - Whether to show submit button next to input field.
     * @property {boolean} [doSubmitCallbackOnBlur]
     * @property {boolean} [keepValueOnSubmit] - If true, do not remove the value from the input field after
     * submit callback has been called.
     * @property {Function} [inputCallback] - Optional callback which get called on input event.
     * @property {Function} [pasteCallback] - Optional callback to call content is pasted into input field.
     * @property {Function} [deleteCallback] - Optional callback that triggers a 'delete' action external to
     * this component. Adds a delete button if defined.
     * @property {HTMLElement} inputElement - Read only
     * @property {import('svelte').Snippet} [specialActions]
     */

    /** @type {Props} */
    let {
        value = $bindable(''),
        placeholder = '',
        label = '',
        formName = null,
        type = 'text',
        autoFocus = false,
        skipValidation = false,
        enableHTMLshortcuts = false,
        noAutoCapitalize = false,
        noAutoCorrect = false,
        autoCompleteTokens = 'on',
        inputMode = null,
        disabled = false,
        isReadOnly = false,
        maxLength = null,
        minLength = null,
        min = null,
        max = null,
        hasUppercase = false,
        displayInline = false,
        isWide = false,
        isXL = false,
        submitCallback = null,
        showSubmitButton = null,
        doSubmitCallbackOnBlur = false,
        keepValueOnSubmit = true,
        inputCallback = null,
        pasteCallback = null,
        deleteCallback = null,
        inputElement = $bindable(),
        specialActions
    } = $props();

    const autocomplete = $derived(noAutoCorrect ? 'off' : autoCompleteTokens)

    const hasSubmitButton = $derived(showSubmitButton === null ? submitCallback !== null : showSubmitButton)

    // rawValue is shown in the input field. Initialize it with value
    let rawValue = $state(value)

    // Whenever rawValue is updated, make it safe to use and update value
    $effect(() => {
        if (skipValidation) {
            value = rawValue.trim()
        }

        // Strip unwanted things like <script> tags. Unescape is here to make characters like & work.
        value = Util.unescape(Util.renderContentSafely(rawValue)).trim()
    })

    const cid = _.uniqueId()

    const submitIcon = getIcon()

    let dummyElement
    let resizedWidth = $state(null)

    let isInvalid = $state(false)

    export function triggerSubmitCallback(e) {
        if (submitCallback) {
            if (!doSubmitCallbackOnBlur && e && e.type === 'focusout') {
                return
            }
            checkValidity()
            if (isInvalid) {
                return
            }
            submitCallback(value)
            if (!keepValueOnSubmit) {
                rawValue = ''
            }
        }
    }

    export function getValue() {
        return value
    }

    export function setValue(newValue) {
        rawValue = newValue
    }

    export function clearValue() {
        rawValue = ''
        triggerInputCallback()
    }

    export function setInvalidState() {
        isInvalid = true
    }

    // Focus the input field, with the caret placed at the end of its value.
    export function setFocus() {
        InputUtil.placeCaret(inputElement)
    }

    function onInput(event) {
        setInputWidth(event.target)
        triggerInputCallback()
    }

    async function triggerInputCallback() {
        await tick()
        clearValidityState()
        if (inputCallback) {
            inputCallback(value)
        }
    }

    function triggerPasteCallback(e) {
        clearValidityState()
        if (pasteCallback && e) {
            pasteCallback(e)
        }
    }

    function onKeyUp(e) {
        if (e.keyCode === 13 || e.key === 'Enter') {
            e.preventDefault()
            triggerSubmitCallback()
        }
    }

    function checkValidity() {
        if (maxLength > 0 && rawValue.length > maxLength) {
            isInvalid = true
        }
        // If a min length is defined, do not invalidate an input length of 0.
        // This way the user can clear the input, which is usefull for search inputs.
        if (minLength > 0 && rawValue.length < minLength && rawValue.length > 0) {
            isInvalid = true
        }
        if (type === 'email' && !InputUtil.validateEmailInput(rawValue)) {
            isInvalid = true
        }
        if (type === 'url' && !InputUtil.validateURLInput(rawValue)) {
            isInvalid = true
        }
        if (type === 'number' && Number.isFinite(min) && parseFloat(rawValue) < min) {
            isInvalid = true
        }
        if (type === 'number' && Number.isFinite(max) && parseFloat(rawValue) > max) {
            isInvalid = true
        }
    }

    function clearValidityState() {
        isInvalid = false
    }

    function enterHTMLShortcut(e) {
        if (!enableHTMLshortcuts) {
            return
        }
        InputUtil.enterHTMLShortcut(e)
    }

    async function setAutoFocus(el) {
        if (autoFocus) {
            // Wait one tick before trying to set focus in input, since the component might not be fully
            // rendered at the moment .setAutoFocus() is called.
            await tick()
            InputUtil.placeCaret(el)
        }
    }

    async function setInputWidth(el) {
        if (!displayInline) {
            resizedWidth = null
            return
        }

        if (!dummyElement) {
            dummyElement = el.previousElementSibling
        }
        await tick()
        resizedWidth = dummyElement.clientWidth + 2
    }

    function getIcon() {
        switch (type) {
            case 'search':
                return 'search'
        }
        return 'checkmark'
    }

    // Set input type after creating the element since Svelte doesn't allow a two-way binding for the type attribute.
    function setType(el) {
        el.type = type
    }
</script>

<div
    class="container"
    class:container--is-disabled={disabled}
    class:container--is-inline={displayInline}
    class:container--is-wider={isWide}
    class:container--is-xl={isXL}
>
    {#if label}
        <label for={cid}>{label}</label>
    {/if}

    <div class="input-container">
        {#if displayInline}
            <span class="inline-text-width-measure">
                {placeholder.length > rawValue?.length ? placeholder : rawValue}
            </span>
        {/if}
        <input
            id={cid}
            bind:value={rawValue}
            bind:this={inputElement}
            {placeholder}
            inputmode={inputMode}
            {disabled}
            onkeydown={enterHTMLShortcut}
            onkeyup={onKeyUp}
            onpaste={triggerPasteCallback}
            onfocusout={triggerSubmitCallback}
            oninput={onInput}
            autocorrect={noAutoCorrect ? 'off' : null}
            {autocomplete}
            spellcheck={noAutoCorrect ? false : null}
            data-gramm_editor={noAutoCorrect ? false : null}
            data-enable-grammarly={noAutoCorrect ? false : null}
            data-gramm={noAutoCorrect ? false : null}
            autocapitalize={noAutoCapitalize ? 'off' : null}
            maxlength={maxLength}
            minlength={minLength}
            {min}
            {max}
            readonly={isReadOnly}
            title={label ? '' : placeholder}
            name={formName}
            class:has-uppercase={hasUppercase}
            class:invalid={isInvalid}
            style:width="{resizedWidth}px"
            size="1"
            use:setType
            use:setAutoFocus
            use:setInputWidth
        />

        {#if maxLength}
            <span class="char-count decoration">{rawValue?.length ?? 0} / {maxLength}</span>
        {/if}

        {@render specialActions?.()}
        {#if hasSubmitButton}
            <button class="decoration" onclick={triggerSubmitCallback}><Icon name={submitIcon}></Icon></button>
        {/if}
        {#if deleteCallback}
            <button class="decoration" onclick={deleteCallback}><Icon name="trash"></Icon></button>
        {/if}
    </div>
</div>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.input-container {
  display: flex;
  width: 100%;
  position: relative;
}
.input-container input {
  border-radius: 16px;
  padding: 14px 16px;
  border: solid 1px hsl(40, 4%, 84%);
  background-color: #fff;
  font-size: 16px;
}
.input-container input:focus-within {
  border-color: hsl(205, 60%, 44%);
  outline: none;
}
.input-container input:disabled {
  cursor: not-allowed;
  user-select: none;
}
.input-container input {
  flex-grow: 1;
}
.input-container input.has-uppercase {
  text-transform: uppercase;
}
.input-container input.has-uppercase::placeholder {
  text-transform: none;
}
.input-container input.invalid {
  border-color: #fc7787;
}
.input-container input:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.input-container .inline-text-width-measure {
  border-radius: 16px;
  padding: 14px 16px;
  border: solid 1px hsl(40, 4%, 84%);
  background-color: #fff;
  font-size: 16px;
}
.input-container .inline-text-width-measure:focus-within {
  border-color: hsl(205, 60%, 44%);
  outline: none;
}
.input-container .inline-text-width-measure {
  position: absolute;
  top: 0;
  visibility: hidden;
  white-space: pre;
}
.input-container .char-count {
  display: flex;
  align-items: center;
  padding: 8px;
  user-select: none;
  color: #9a9bb5;
  background-color: #fff;
  border: solid 1px hsl(40, 4%, 84%);
  border-left: 0;
}
.input-container button {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.input-container button :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.input-container button {
  color: #fff;
  background-color: #122d40;
  border: solid 1px #122d40;
}
.input-container button > :global(*) {
  display: inline-flex;
}
.input-container button > :global(*:empty) {
  display: none;
}
@media (hover: hover) {
  .input-container button:hover {
    background-color: hsl(205, 60%, 44%);
    border-color: hsl(205, 60%, 44%);
    color: #fff;
  }
  .input-container button:hover :global(svg) {
    fill: #fff;
  }
}
.input-container button:active {
  background-color: hsl(205, 58%, 38%);
  border-color: hsl(205, 58%, 38%);
  color: #fff;
}
.input-container button:active :global(svg) {
  fill: #fff;
}
.input-container button {
  flex-shrink: 0;
  border-radius: 0;
}
.input-container .decoration:last-child {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
}

.container {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.container--is-disabled {
  opacity: 0.75;
}
.container--is-inline {
  display: inline-flex;
  width: auto;
  margin: 2px 0;
  max-width: 100%;
}
.container--is-inline input {
  min-width: 100px;
}
.container--is-wider input {
  min-width: 200px;
}
.container--is-xl input {
  font-size: 26px;
  font-weight: 600;
}
.container--is-xl :global(svg) {
  width: 24px;
  height: 24px;
}
.container label {
  font-size: 14px;
  font-variant-caps: all-small-caps;
  letter-spacing: 1px;
  line-height: 12px;
  color: hsl(205, 60%, 44%);
  display: flex;
  justify-content: space-between;
  max-width: 100%;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: hsl(205, 60%, 44%);
}</style>
